import { useState } from "react";
import { toast } from "sonner";
import { SquareDashedBottom } from "lucide-react";
import { Switch } from "../ui/switch";
import { TableCell, TableRow } from "../ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { ClearSampleRowInputsDialog } from "./ClearSampleRowInputsDialog";
import { InputBindingCell } from "./InputBindingCell";
import { type DocumentRowAnalytics } from "../../hooks/audit/useDocumentRowAnalytics";
import {
  ANALYTICS_INPUT_COPY,
  INPUT_BINDING_COPY,
  hasSampleReachColumn,
  sampleRowReachLabel,
  targetKey,
  targetsFilledBy,
  type BindingTarget,
  type BoundDocumentId,
  type TestTableSourceRow,
} from "../../lib/analyticsFeeds";

/**
 * The row for what the run builds out of the population — one row per sample.
 *
 * Sample basis only. A direct-basis table's single row is a placeholder seeded
 * when the session is created, and no surface writes its attribute values: the
 * sample list that edits them renders only for a sample basis, and so does the
 * work paper's attribute column. Offering the feed there would bind a parameter
 * to a header line over the word "placeholder".
 *
 * It leads the table because it is the one input that is there before any file
 * is uploaded, and it is drawn as what it is: a dashed marker in the `Title`
 * cell where a document row shows its `FileIcon`, no row click to select it, and
 * `generated at run time` where a category would name what the file is. Every
 * one of those says the same thing — there is no document here to select or
 * categorise.
 *
 * Its switch in the `For Analytics Test` column marks no file. It says whether
 * this row answers any of the workflow's inputs, and it is derived rather than
 * stored: on while any input is bound to the sample row, and otherwise the
 * auditor's choice here, which is held by this component and never saved.
 * Before any choice it is on while some input is unanswered, because this row
 * is the only way to answer one per sample. Off hides the picker, and switching
 * off over bindings clears them after a confirmation, because a hidden binding
 * would still feed every run.
 *
 * It exists so the sample's own row can be the *subject* of a row rather than an
 * option on some other file's. The rule the table obeys is that no row may map a
 * file other than the file it is for, and a run-time answer offered from a
 * document's row breaks it in the one direction that cannot be checked: the
 * binding names no file, and the row it was chosen from names one.
 *
 * The Support tab only. The Documents tab is an inventory of files the workspace
 * holds, and this is not one of them.
 */
export function SampleRowInputRow({
  analytics,
  documentNames,
  sourceRows,
}: {
  analytics: DocumentRowAnalytics;
  documentNames: ReadonlyMap<BoundDocumentId, string>;
  /** Every sample of the test table, which is the total the column reads against. */
  sourceRows: readonly TestTableSourceRow[];
}) {
  // Read once for every place the row names itself — the line the auditor reads
  // and the accessible names of its switch and parameters trigger — so no
  // control can announce a subject under a name the row does not carry.
  const title = INPUT_BINDING_COPY.sampleRowTitle;
  const filled = targetsFilledBy(analytics.targets, { kind: "sample_row" });
  // Null until the auditor operates the switch, so the default keeps following
  // the targets as they load rather than freezing on the empty list a read in
  // flight looks like.
  const [chosen, setChosen] = useState<boolean | null>(null);
  const [confirmingClear, setConfirmingClear] = useState<
    readonly BindingTarget[] | null
  >(null);
  // Without an answer from the feeds read, neither position is known: a
  // sample-row binding may exist that the list has not delivered. The picker
  // stays up to report the read, as it does on every other row.
  const feedsAnswered =
    analytics.feedsState === "ready" || analytics.feedsState === "stale";
  const opensByDefault = analytics.targets.some(
    (target) => target.boundSource === null,
  );
  const used = filled.length > 0 || (chosen ?? opensByDefault);
  const switchName = `${ANALYTICS_INPUT_COPY.flagColumnHeading} — ${title}`;

  const handleCheckedChange = (next: boolean) => {
    if (next || filled.length === 0) {
      setChosen(next);
    } else {
      setConfirmingClear(filled);
    }
  };

  const handleConfirmClear = () => {
    if (confirmingClear === null) {
      return;
    }
    // Only pairs the rendered list still shows as this row's: one rebound
    // elsewhere since the dialog opened is someone else's answer now, and the
    // clear is keyed on the pair, not on who fills it. This list is only as
    // fresh as the last feeds read, so it narrows what is sent rather than
    // settling it — the write re-reads and decides there.
    const named = new Set(confirmingClear.map(targetKey));
    const stillFilled = filled.filter((target) => named.has(targetKey(target)));
    setConfirmingClear(null);
    if (stillFilled.length === 0) {
      // The auditor confirmed a clear that has nothing left to clear, and the
      // switch going quietly off would read as the bindings having been removed
      // here rather than moved elsewhere.
      toast.info("Those inputs are no longer filled by each sample's own row.");
      setChosen(false);
      return;
    }
    // The choice follows the write rather than the press. A pair that survived
    // a refused clear still fills this row, and recording `off` over it would
    // hold the row to a claim the bindings contradict — the switch would read
    // the auditor's answer while the run keeps being fed. An unfinished clear
    // leaves the choice unmade, so the row goes on following its bindings. The
    // empty answer covers both outcomes that leave this row filling nothing,
    // and the write reports which of them the auditor got.
    void analytics.onClearAll(stillFilled).then((remaining) => {
      if (remaining.length === 0) {
        setChosen(false);
      }
    });
  };

  return (
    <TableRow className="hover:bg-transparent">
      <TableCell className="border-b text-sm italic text-font-secondary">
        {INPUT_BINDING_COPY.sampleRowDocument}
      </TableCell>
      <TableCell className="border-b text-sm">
        <div className="flex flex-row items-start gap-2">
          <SquareDashedBottom
            aria-hidden="true"
            className="mt-0.5 h-4 w-4 shrink-0 text-font-secondary"
          />
          <div className="flex flex-col">
            <span>{title}</span>
            <span className="text-xs italic text-font-secondary">
              {INPUT_BINDING_COPY.sampleRowCaption}
            </span>
          </div>
        </div>
      </TableCell>
      <TableCell className="border-b">
        {/* The document rows' switch and state words, drawn here rather than
            through `AnalyticsInputToggle`: that control describes a file — its
            name calls the row evidence and its gates read a file format — and
            this row has none. */}
        <div className="flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              {/* span: see `AnalyticsInputToggle`. */}
              <span
                tabIndex={analytics.writesHeldReason !== null ? 0 : undefined}
                className="inline-flex"
              >
                <Switch
                  checked={feedsAnswered && used}
                  // Every binding write, not only this row's: any of them can
                  // change which inputs the sample row fills, and the
                  // confirmation names those inputs.
                  disabled={
                    analytics.pending ||
                    !feedsAnswered ||
                    analytics.writesHeldReason !== null
                  }
                  aria-label={
                    feedsAnswered
                      ? switchName
                      : `${switchName} — ${ANALYTICS_INPUT_COPY.flagUnknown}`
                  }
                  onCheckedChange={handleCheckedChange}
                />
              </span>
            </TooltipTrigger>
            {analytics.writesHeldReason !== null && (
              <TooltipContent>{analytics.writesHeldReason}</TooltipContent>
            )}
          </Tooltip>
          <span aria-hidden="true" className="text-sm text-font-secondary">
            {!feedsAnswered
              ? ANALYTICS_INPUT_COPY.flagUnknown
              : used
                ? ANALYTICS_INPUT_COPY.flagOn
                : ANALYTICS_INPUT_COPY.flagOff}
          </span>
        </div>
        <ClearSampleRowInputsDialog
          targets={confirmingClear ?? []}
          open={confirmingClear !== null}
          onOpenChange={(open) => {
            if (!open) {
              setConfirmingClear(null);
            }
          }}
          onConfirm={handleConfirmClear}
        />
      </TableCell>
      <TableCell className="border-b p-2">
        {!feedsAnswered || used ? (
          <InputBindingCell
            subject={{ kind: "sample_row", onFill: analytics.onUseSampleRow }}
            subjectName={title}
            targets={analytics.targets}
            documentNames={documentNames}
            feedsState={analytics.feedsState}
            deletedColumnNames={analytics.deletedColumnNames}
            pending={analytics.pending}
            writesHeldReason={analytics.writesHeldReason}
            onClear={analytics.onClear}
          />
        ) : (
          <div className="p-2">
            <span className="text-xs italic text-font-secondary">
              {INPUT_BINDING_COPY.sampleRowNotUsed}
            </span>
          </div>
        )}
      </TableCell>
      {/* Every sample carries this row, so the column states the reach it has by
          definition rather than a count that happens to equal the total. The
          cell comes and goes with the column; the row does not, because it is
          the only subject a run-time parameter can be answered from. */}
      {hasSampleReachColumn(sourceRows) && (
        <TableCell className="border-b">
          <span className="text-xs italic text-font-secondary">
            {sampleRowReachLabel(sourceRows)}
          </span>
        </TableCell>
      )}
      <TableCell className="border-b" />
      <TableCell className="border-b" />
    </TableRow>
  );
}
